import { useMemo, useState } from "react";
import {
  ChevronDown,
  Gift,
  Heart,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { useCart } from "../../../../context/CartContext";
import { useWishlist } from "../../../../context/WishlistContext";
import { ProductType } from "../../../../Redux/custmoer_cart_service/types";
import { formatPrice } from "../RefProductDetailTypes";
import "./ProductPurchaseCard.css";

export interface ProductPurchaseCardProps {
  productId: string;
  productTitle: string;
  productImage: string;
  productDescription?: string;
  price: number;
  mrp?: number;
  ssin?: string;
  sellerName?: string;
  sellerSlug?: string;
  sellerId?: string;
  stock?: boolean;
  packaging?: string;
  payment?: string;
  giftOptions?: string;
  category?: string;
  returnPolicy?: string;
  warranty?: string;
  voucherAmount?: number;
  voucherValidityDays?: number;
  autoRepurchaseAmount?: number;
  onAddToCart?: (quantity: number) => Promise<void> | void;
  onBuyNow?: (quantity: number) => Promise<void> | void;
  onWishlist?: () => void;
}

const ProductPurchaseCard = ({
  productId,
  productTitle,
  productImage,
  productDescription,
  price,
  mrp,
  ssin,
  sellerName = "Salar Store",
  sellerSlug,
  sellerId,
  stock = true,
  payment = "Secure transaction",
  returnPolicy = "Voucher Guarantee & Support",
  voucherAmount,
  voucherValidityDays,
  autoRepurchaseAmount,
  onAddToCart,
  onBuyNow,
  onWishlist,
}: ProductPurchaseCardProps) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart, setCartOpen, isProductInCart, cartItems } = useCart();
  const { toggleWishlist, isItemWishlisted } = useWishlist();

  const [quantity, setQuantity] = useState(1);
  const [addingCart, setAddingCart] = useState(false);

  const inCart = useMemo(() => {
    if (!productId) return false;
    const foundInList = (cartItems || []).some((item: any) => {
      const itemProdId = String(item.productId || item._id || item.id || "");
      if (itemProdId === productId) return true;
      if (ssin && item.variantId && String(item.variantId) === String(ssin))
        return true;
      return false;
    });
    return foundInList || isProductInCart(productId, ssin);
  }, [productId, ssin, cartItems, isProductInCart]);

  const isWishlisted = Boolean(
    productId && (isItemWishlisted(productId, ssin) || isItemWishlisted(productId))
  );

  const handleAddToCartClick = async () => {
    if (!stock || addingCart) return;

    if (!isAuthenticated) {
      navigate("/SignIn");
      return;
    }

    if (inCart) {
      setCartOpen(true);
      return;
    }

    setAddingCart(true);
    try {
      if (onAddToCart) {
        await onAddToCart(quantity);
      } else {
        await addToCart({
          productId: String(productId),
          productName: productTitle,
          productImage: productImage,
          productType: ProductType.MLM_VOUCHER,
          variantId: ssin || productId,
          variantName: productTitle,
          quantity,
          unitPrice: price,
          price,
          mrp: mrp || price,
          sellerId: sellerId || sellerName,
          sellerName: sellerName !== "-" ? sellerName : undefined,
          isMLMProduct: true,
          metadata: {
            mrp: mrp || price,
            image: productImage,
            description: productDescription,
          },
        });
        setCartOpen(true);
      }
    } finally {
      setAddingCart(false);
    }
  };

  const handleBuyNowClick = async () => {
    if (!stock) return;

    if (!isAuthenticated) {
      navigate("/SignIn");
      return;
    }

    if (onBuyNow) {
      await onBuyNow(quantity);
      return;
    }

    await addToCart({
      productId: String(productId),
      productName: productTitle,
      productImage: productImage,
      productType: ProductType.MLM_VOUCHER,
      variantId: ssin || productId,
      variantName: productTitle,
      quantity,
      unitPrice: price,
      price,
      mrp: mrp || price,
      sellerId: sellerId || sellerName,
      sellerName: sellerName !== "-" ? sellerName : undefined,
      isMLMProduct: true,
      metadata: {
        mrp: mrp || price,
        image: productImage,
        description: productDescription,
      },
    });
    navigate("/Cart");
  };

  const handleWishlistClick = () => {
    if (onWishlist) {
      onWishlist();
      return;
    }
    toggleWishlist(String(productId), {
      ssin: ssin || undefined,
      productVariantId: ssin || undefined,
    });
  };

  const resolvedSellerSlug =
    sellerSlug ||
    (sellerName !== "-"
      ? sellerName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
      : "");

  const discountPercent =
    mrp && mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

  return (
    <div className="purchase-card-wrapper">
      <aside className="purchase-card">
        {/* Price Row */}
        <div className="purchase-price">
          <sup>₹</sup>
          <strong>{formatPrice(price)}</strong>
        </div>

        {mrp && mrp > price ? (
          <div style={{ marginTop: "4px", fontSize: "12px", color: "#565959" }}>
            M.R.P.: <del>₹{formatPrice(mrp)}</del>
            {discountPercent > 0 && (
              <span
                style={{
                  color: "#cc0c39",
                  fontWeight: 700,
                  marginLeft: "6px",
                }}
              >
                ({discountPercent}% off)
              </span>
            )}
          </div>
        ) : null}

        {voucherAmount && voucherAmount > 0 ? (
          <div
            style={{
              marginTop: "10px",
              padding: "8px 10px",
              borderRadius: "8px",
              background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
              border: "1px solid #bfdbfe",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "12px",
              fontWeight: 650,
              color: "#1d4ed8",
            }}
          >
            <Gift size={15} />
            <span>
              ₹{formatPrice(voucherAmount)} Shopping Voucher
              {voucherValidityDays ? ` • ${voucherValidityDays}d validity` : ""}
            </span>
          </div>
        ) : null}

        {/* Digital Product Delivery Notice */}
        <div className="purchase-delivery-message">
          <div className="purchase-delivery-title">
            <Zap size={18} strokeWidth={2} style={{ color: "#2563eb" }} />
            <p>
              <strong>Instant Digital Product</strong>. Vouchers and referral benefits are activated immediately in your account upon order completion.
            </p>
          </div>
        </div>

        {/* Stock Status */}
        <div
          className={`purchase-stock ${
            !stock ? "unavailable" : "available"
          }`}
        >
          {!stock ? "Currently Unavailable" : "Available Now • Instant Access"}
        </div>

        {/* Quantity Selector */}
        {stock && (
          <div className="purchase-mobile-controls">
            <div className="purchase-divider purchase-controls-divider" />
            <div className="purchase-quantity">
              <label htmlFor="purchase-quantity">Quantity</label>
              <div className="purchase-quantity-select">
                <select
                  id="purchase-quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                >
                  {[1, 2, 3, 4, 5].map((q) => (
                    <option key={q} value={q}>
                      {q}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} strokeWidth={2} />
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons: Add to Cart & Buy Now */}
        <div className="purchase-actions">
          <button
            type="button"
            className="purchase-cart-btn"
            disabled={!stock || addingCart}
            onClick={handleAddToCartClick}
          >
            {addingCart ? "Adding..." : inCart ? "View In Cart" : "Add to Cart"}
          </button>

        
        </div>

        <div className="purchase-divider" />

        {/* Meta Info List */}
        <div className="purchase-info-list">
          <div>
            <span>Product Type</span>
            <strong>Digital Referral Voucher</strong>
          </div>

          <div>
            <span>Access</span>
            <strong style={{ color: "#16a34a" }}>
              Instant Wallet Credit
            </strong>
          </div>

          <div className="purchase-seller-row">
            <span>Sold by</span>
            {resolvedSellerSlug ? (
              <button
                type="button"
                className="purchase-seller-link"
                onClick={() => navigate(`/seller/${resolvedSellerSlug}`)}
              >
                <strong>{sellerName}</strong>
                <ShieldCheck size={14} strokeWidth={1.9} />
              </button>
            ) : (
              <strong>{sellerName}</strong>
            )}
          </div>

          <div>
            <span>Payment</span>
            <strong>{payment}</strong>
          </div>

          {voucherAmount && voucherAmount > 0 ? (
            <div>
              <span>Shopping Voucher</span>
              <strong style={{ color: "#16a34a" }}>
                ₹{formatPrice(voucherAmount)}
              </strong>
            </div>
          ) : null}

          {autoRepurchaseAmount && autoRepurchaseAmount > 0 ? (
            <div>
              <span>Auto Repurchase</span>
              <strong>₹{formatPrice(autoRepurchaseAmount)}</strong>
            </div>
          ) : null}

          <div>
            <span>Support</span>
            <strong>{returnPolicy}</strong>
          </div>
        </div>

        <div className="purchase-divider" />

        {/* Value / Benefits List */}
        <div className="purchase-benefits">
          <div className="purchase-benefit-item">
            <span className="purchase-benefit-icon">
              <ShieldCheck size={19} strokeWidth={1.8} />
            </span>
            <div>
              <strong>100% Authentic Referral Product</strong>
              <span>Verified partner quality & benefits assured</span>
            </div>
          </div>

          <div className="purchase-benefit-item">
            <span className="purchase-benefit-icon">
              <Zap size={19} strokeWidth={1.8} />
            </span>
            <div>
              <strong>Instant Digital Access</strong>
              <span>Immediate account activation & voucher credit</span>
            </div>
          </div>

          {voucherAmount && voucherAmount > 0 ? (
            <div className="purchase-benefit-item">
              <span className="purchase-benefit-icon">
                <Gift size={19} strokeWidth={1.8} />
              </span>
              <div>
                <strong>Salar Voucher Benefits</strong>
                <span>Instant reward credited upon order confirmation</span>
              </div>
            </div>
          ) : null}
        </div>

     
      </aside>
    </div>
  );
};

export default ProductPurchaseCard;
